import { store } from '@redux/store';
import GoogleAnalyticsCustomDimensions from 'constants/googleAnalyticsCustomDimension';
import GoogleAnalyticsEvent from 'dataTransferObjects/googleAnalyticsEvent';
import { userProfileActions } from 'features/userProfile';
import { selectModal } from 'layout/redux/selectors';
import { backButton, privacyPolicyParagraph } from '../constants/sharedHtml';
import leadConstructor from '../services/leadConstructor';
import { postLeadTourForm } from '../services/taskFlow';
import ContactRadioGroup from './contactRadioGroup';
import DateInput from './dateInput';
import EmailInput from './emailInput';
import NameInput from './nameInput';
import PhoneInput from './phoneInput';
import RequestButton from './requestButton';
import TourRadioOptions from './tourRadioOptions';
import ZipInput from './zipInput';
import LegalText from './legalText';

/* Creates a new instance of a Request Tour Form to allow users making an appointment to tour the selected housing. */
export default class RequestTourForm {
    constructor() {
        const userProfile = store.getState()?.userProfile || {};
        const state = store.getState();
        const { id, guid, type } = selectModal(state);
        this.id = id;
        this.guid = guid;
        this.type = type;
        this.excludeRecommendations = true;
        this.isPersistent = true;
        this.requestButton = new RequestButton();
        this.emailAddressInput = new EmailInput();
        this.emailAddressInput.value = userProfile.email;
        this.formId = 'requestATourPaid';
        this.fullNameInput = new NameInput();
        this.fullNameInput.value = userProfile.name;
        this.phoneNumberInput = new PhoneInput();
        this.phoneNumberInput.value = userProfile.phone;
        this.postalCodeInput = new ZipInput();
        this.postalCodeInput.value = userProfile.zip;
        this.postalCodeInput.isInternational = userProfile.livesOutside;
        this.contactRadioGroup = new ContactRadioGroup();
        this.contactRadioGroup.value = userProfile.preferredContactMethod;
        this.tourRadioOptions = new TourRadioOptions();
        this.dateInput = new DateInput();
        this.onClickEvent = null;
        this.legalText = new LegalText(privacyPolicyParagraph);
    }

    static validateNode(node) {
        return node.validate();
    }

    static getFormOpenedAnalyticsData(properties) {
        const customDimensions = {};

        if (properties.marketId) {
            customDimensions[[GoogleAnalyticsCustomDimensions.MarketId().getName()]] = properties.marketId;
        }

        if (properties.marketName && properties.stateAbbreviation) {
            customDimensions[[GoogleAnalyticsCustomDimensions.MarketName().getName()]] = `${properties.marketName}, ${properties.stateAbbreviation}`;
        }

        if (properties.city) {
            customDimensions[[GoogleAnalyticsCustomDimensions.City().getName()]] = properties.city;
        }

        return new GoogleAnalyticsEvent(
            'Lead_Events',
            'Search_Results_CTA_Request_Tour',
            'Open_Form_Request_Tour',
            customDimensions);
    }

    setHousingData(properties = {}) {
        this.housingData = properties;
    }

    async submitBrochureForm(event) {
      event.preventDefault();

      const validationFunctions = [
        this.emailAddressInput,
        this.fullNameInput,
        this.postalCodeInput,
        this.dateInput,
        this.tourRadioOptions,
        this.phoneNumberInput,
      ];

      this.requestButton.getNode().disabled = 'disabled';

      const results = await Promise.all(
        validationFunctions.map((input) => RequestTourForm.validateNode(input))
      );

      if (results.some((isValid) => !isValid)) {
        this.requestButton.getNode().disabled = false;
        return;
      }

      const formData = new FormData(this.getForm());
      const brochureInformation = Object.fromEntries(formData.entries());

      brochureInformation.id = this.id;
      brochureInformation.type = `${this.type}-tour`;
      brochureInformation.excludeRecommendations = this.excludeRecommendations;
      brochureInformation.guid = this.guid;

      const brochureLeadObject = leadConstructor(brochureInformation);
      brochureLeadObject.setHousingInformation?.(this.housingData.listingInformation);

      postLeadTourForm(brochureLeadObject);
    }

  /**
   * handleEmailValidationResult: Handles the result of the email validation.
   * @param {Object} isValid The result of the email validation.
   */
  handleEmailValidationResult(isValid) {
    this.requestButton.getNode().disabled = false;
    if (isValid) {
      this.emailAddressInput.setValidState(this.emailAddressInput.getNode());
      this.emailAddressInput.toggleEmailValidationDataAttribute(true);
    } else {
      this.emailAddressInput.setInvalidState(this.emailAddressInput.getNode());
      this.emailAddressInput.toggleEmailValidationDataAttribute(false);
    }
  }

    bindEvents() {
        this.onClickEvent = this.submitBrochureForm.bind(this);
        this.phoneNumberInput.bindEvents();
        this.postalCodeInput.bindEvents();
        this.legalText.bindEvents();
        this.requestButton.getNode().addEventListener('click', this.onClickEvent);
        this.contactRadioGroup.onChange(this.onPreferredContactMethodChange.bind(this));

        const userProfile = store.getState()?.userProfile || {};
        this.setRequiredAttributeInPhone(userProfile.preferredContactMethod);
        this.bindChangeEvents();
    }

    /**
     * bindChangeEvents: Get the form inputs then subscribe each OnChangeEvent to store and persist their values
     */
    bindChangeEvents() {
      const changeInputs = [
          this.emailAddressInput,
          this.fullNameInput,
          this.postalCodeInput,
          this.phoneNumberInput,
      ];

      changeInputs.forEach(input => input.getOnChangeEvent().subscribe(data =>
          store.dispatch(userProfileActions.update(data))));
    }

    onPreferredContactMethodChange(event) {
        const value = event.target.value;
        this.setRequiredAttributeInPhone(value);
    }

    setRequiredAttributeInPhone(value) {
        if (value === 'Phone' || value === 'Text Message') {
            this.phoneNumberInput.setRequired();
            return;
        }

        this.phoneNumberInput.removeRequired();
    }

    unbindEvents() {
        this.requestButton.getNode().removeEventListener('click', this.onClickEvent);
        this.legalText.unbindEvents();
    }

    getForm() {
        return document.getElementById(this.formId);
    }

    getHtml() {
      this.legalText.setReplacements([
        { key: "{0}", value: "Submit Request"},
        { key: "{1}", value: this.housingData.listingInformation?.brandName ?? "the builder" },
      ]);
        return `
    <div class="lead-form form--brochure">
      ${this.isPersistent ? backButton : ``}
      <p class="form__title">Request A Tour</p>
      <form id='${this.formId}'>
        <fieldset>
          <div class="form__field field--radiogroup gap--16" aria-labeledby="contact-type-label">
            <span class="visuallyhidden" id="contact-type-label">Appointment Type</span>
            <div class="flex--column align--start">
              ${this.tourRadioOptions.getHtml()}
            </div>
            <div class="field--datetime flex--column align--start">
              ${this.dateInput.getHtml()}
            </div>
          </div>
          <div class="form__field">
            ${this.fullNameInput.getHtml()}
          </div>
          <div class="form__field">
            ${this.emailAddressInput.getHtml()}
          </div>
          <div class="form__field">
            ${this.phoneNumberInput.getHtml()}
          </div>
          <div class="form__field field--split">
            ${this.postalCodeInput.getHtml()}
          </div>
          <div class="form__field field--radiogroup" aria-labeledby="preferred-label">
            <span class="form__label" id="preferred-label">Preferred Contact Method</span>
            <div>
              ${this.contactRadioGroup.getHtml()}
            </div>
        </div>
          <div class="form__button-bar">
            ${this.requestButton.getHtml()}
          </div>
        </fieldset>        
      </form>
      ${this.legalText.getHtml()}
    </div>`;
    }
}
